import * as React from 'react';
import PropTypes from 'prop-types';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDownIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

// Accordion Header Component for title and description
const AccordionHeader = React.forwardRef(({ className, title, description, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'mb-6 p-4 rounded-2xl',
      'bg-gray-50 text-blue-900 border border-gray-200',
      'dark:bg-gray-900 dark:text-cyan-100 dark:border-gray-800',
      className
    )}
    {...props}
  >
    {title && (
      <h3 className="text-lg font-medium font-roboto-condensed leading-6 mb-1">
        {title}
      </h3>
    )}
    {description && (
      <p className="text-base font-normal font-roboto-condensed leading-6 text-gray-600 dark:text-gray-400">
        {description}
      </p>
    )}
  </div>
));
AccordionHeader.displayName = 'AccordionHeader';
AccordionHeader.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
};

const Accordion = React.forwardRef(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Root
    ref={ref}
    className={cn('w-full', className)}
    {...props}
  >
    {children}
  </AccordionPrimitive.Root>
));
Accordion.displayName = 'Accordion';
Accordion.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  type: PropTypes.oneOf(['single', 'multiple']),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  onValueChange: PropTypes.func,
  collapsible: PropTypes.bool,
  disabled: PropTypes.bool,
};

const AccordionItem = React.forwardRef(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    data-slot="accordion-item"
    className={cn(
      'bg-gray-50 border border-gray-200 rounded-2xl shadow-sm mb-4 last:mb-0 overflow-hidden',
      'dark:bg-gray-800 dark:border-gray-700',
      className
    )}
    {...props}
  >
    {children}
  </AccordionPrimitive.Item>
));
AccordionItem.displayName = 'AccordionItem';
AccordionItem.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  value: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};

const AccordionTrigger = React.forwardRef(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      data-slot="accordion-trigger"
      className={cn(
        'focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 items-center justify-between gap-4 px-4 py-4 text-left font-medium transition-all outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180',
        'text-blue-900 text-lg leading-6 font-roboto-condensed hover:bg-gray-100',
        'dark:text-cyan-100 dark:hover:bg-gray-700',
        className
      )}
      {...props}
    >
      {children}
      <ChevronDownIcon className="text-gray-700 dark:text-gray-100 pointer-events-none size-4 shrink-0 transition-transform duration-200" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = 'AccordionTrigger';
AccordionTrigger.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
};

const AccordionContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    data-slot="accordion-content"
    className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down border-t border-gray-200 dark:border-gray-700"
    {...props}
  >
    <div className={cn(
      'px-4 py-4 text-gray-600 dark:text-gray-400 text-base leading-6 font-roboto-condensed font-normal',
      className
    )}>
      {children}
    </div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = 'AccordionContent';
AccordionContent.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export { Accordion, AccordionHeader, AccordionItem, AccordionTrigger, AccordionContent }
